import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import { sendContactForm } from '../services/contactService';
import { theme } from '../styles/theme';
import {
  PageContainer,
  PageTitle,
  Form,
  FormGroup,
  Label,
  Input,
  TextArea,
  Button,
  Alert,
  FormContainer
} from '../styles/components';

// Component-specific styled elements
const ErrorMessage = styled.div`
  color: ${theme.colors.danger};
  margin-top: ${theme.spacing.xs};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled(Alert)`
  margin-bottom: ${theme.spacing.md};
  text-align: center;
`;

// Container with custom max-width for the contact page
const ContactContainer = styled(PageContainer)`
  max-width: 800px;
`;

const ContactInfo = styled.p`
  color: ${theme.colors.lightText};
  font-size: ${theme.fontSizes.md};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;

  a {
    color: ${theme.colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = () => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Navn er påkrevd';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-post er påkrevd';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ugyldig e-post format';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Emne er påkrevd';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Melding er påkrevd';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await sendContactForm(formData);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error sending contact form:', error);
      setErrors({
        ...errors,
        message: 'Det oppstod en feil ved sending av skjemaet. Vennligst prøv igjen senere.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Kontakt oss | Tryllepapp</title>
        <meta name="description" content="Ta kontakt med Tryllepapp. Vi svarer gjerne på spørsmål om kjøp og salg av Magic: The Gathering-kort." />
        <link rel="canonical" href="https://selg.tryllepapp.no/contact" />
      </Helmet>
      <ContactContainer>
        <PageTitle>Kontakt oss</PageTitle>
        <ContactInfo>
          Kontakt oss på dette skjemaet eller send oss en mail på{' '}
          <a href="mailto:kundeservice@tryllepapp.no">kundeservice@tryllepapp.no</a>
        </ContactInfo>

        {submitSuccess && (
          <SuccessMessage variant="success">
            Takk for din henvendelse! Vi vil komme tilbake til deg så snart som mulig.
          </SuccessMessage>
        )}
        
        <FormContainer>
          <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Navn</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="email">E-post</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="subject">Emne</Label>
            <Input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
            />
            {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="message">Melding</Label>
            <TextArea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
            />
            {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
          </FormGroup>
          
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sender...' : 'Send melding'}
          </Button>
          </Form>
        </FormContainer>
      </ContactContainer>
    </Layout>
  );
};

export default ContactPage;
